import React, { useState, useLayoutEffect } from 'react';
import { getCachedRatio, setCachedRatio } from './imageRatioCache';

// One photo's whole frame: the blurred ambient backdrop (+ its fade-in
// veil), the rounded eyepiece window sized to THIS photo's own aspect
// ratio, and the image itself. Three of these are mounted at once, one
// per physical slot (0/1/2) inside PhotoViewer's sliding track — but the
// slot's identity (its React key) is now fixed and permanent, decoupled
// from which photo it happens to be showing. PhotoViewer rotates which
// slot plays prev/current/next as the index advances, reusing whichever
// slot is being vacated for the newly-revealed neighbor, and passes each
// slot its role only via the `order` prop (for visual position — see
// PhotoViewer's transform math, which assumes visual order, not DOM
// order) and via `photo` (which picture to show).
//
// This is deliberate: earlier, each slot WAS keyed by photo index, so
// every commit reassigned two of the three keys, forcing React to move
// those DOM nodes to satisfy the new key order. That's an actual DOM
// reorder, not just a state update — and on mobile, moving a node that
// carries an expensive backdrop-filter (.vm-blur) makes the browser
// re-layerize it, which can cost more than one frame and show up as a
// visible flash right at the moment a slide commits. Keeping slot
// identity stable means the DOM never reorders, so that backdrop layer
// is never torn down and rebuilt — only its `photo`/`order` props change
// in place, same as any other prop update. The consequence: a slot's
// photo CAN now change while it stays mounted, so the reset logic below
// keys off `photo.src` instead of relying on a fresh mount to provide it.
//
// Only the CURRENT (visible, interactive) slot receives live scale/offset/
// dragging from the parent — PhotoViewer owns that state because the pan
// gesture has to hand off to sliding the whole track once a zoomed photo's
// edge reaches its frame (see PhotoViewer's drag handling), which needs a
// pixel measurement of THIS slot's .vm-mat. matRef is how the parent gets
// that measurement; it's only ever passed to the current slot.
const PhotoStage = React.memo(function PhotoStage({
  photo,
  order,
  isCurrent,
  scale,
  offset,
  dragging,
  matRef,
}) {
  // Seeded from the shared ratio cache when this photo's shape is already
  // known — almost always true for skip/jump targets, which are preloaded
  // while the user is still on the previous photo (see PhotoViewer's
  // preload effect) — so the frame renders correctly shaped from the very
  // first paint instead of popping from a placeholder. Only a photo this
  // session has genuinely never seen before falls back to a plausible
  // default, corrected as soon as this instance's own image resolves.
  const [imgRatio, setImgRatio] = useState(() => getCachedRatio(photo.src) || 4 / 3);
  // Covers the ambient backdrop with a warm veil until this photo is
  // actually decoded, then fades it away — see .vm-blur-veil in
  // PhotoViewer.css for why this is a separate, unfiltered layer rather
  // than animating the (expensive) backdrop-filter blur itself.
  const [bgReady, setBgReady] = useState(false);

  // useLayoutEffect, not useEffect: this needs to land before the browser
  // paints. A plain useEffect runs after paint, so the frame that just
  // switched to the new photo.src (img src + backgroundImage) would still
  // be showing the PREVIOUS photo's imgRatio for that one paint -- a
  // visible squish/stretch flash of the new image at the old aspect ratio,
  // worse the longer that gap runs (e.g. under load or a slow connection).
  useLayoutEffect(() => {
    let cancelled = false;
    // This slot's photo can change while it stays mounted (stable
    // per-slot identity — see the note above), so re-arm the veil and
    // re-seed the ratio for the NEW photo up front, the same way a fresh
    // mount's initial state used to cover it. Cheap: getCachedRatio is a
    // plain lookup, and this just means an already-seen photo shows in
    // its correct shape immediately instead of waiting on decode again.
    setBgReady(false);
    setImgRatio(getCachedRatio(photo.src) || 4 / 3);
    const img = new Image();
    img.src = photo.src;
    const reveal = () => {
      if (cancelled) return;
      setBgReady(true);
      // decode()/onload guarantee naturalWidth/Height are populated, so this
      // is also a chance to learn (and share) this photo's ratio, ahead of
      // the visible <img>'s own onLoad below.
      if (img.naturalWidth && img.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        setCachedRatio(photo.src, ratio);
        setImgRatio(ratio);
      }
    };
    if (img.decode) {
      img.decode().then(reveal).catch(reveal);
    } else {
      img.onload = reveal;
    }
    return () => { cancelled = true; };
  }, [photo.src]);

  const handleImgLoad = e => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalWidth && naturalHeight) {
      const ratio = naturalWidth / naturalHeight;
      setCachedRatio(photo.src, ratio);
      setImgRatio(ratio);
    }
  };

  return (
    <div className="vm-photo-slot" style={{ backgroundImage: `url(${photo.src})`, order }}>
      <div className="vm-blur">
        <div className="vm-blur-img" style={{ backgroundImage: `url(${photo.src})` }} />
      </div>
      <div className="vm-blur-veil" style={{ opacity: bgReady ? 0 : 1 }} />
      <div className="vm-edge-fade" />

      <div className="vm-stage">
        <div className="vm-mat" ref={matRef} style={{ aspectRatio: imgRatio }}>
          <div className="photo-container">
            <img
              src={photo.src}
              alt={photo.filename}
              onLoad={handleImgLoad}
              style={{
                cursor: !isCurrent ? 'default' : (dragging ? 'grabbing' : 'grab'),
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default PhotoStage;
